import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

import { ReaImportDataPresenter } from '../presenters/rea-import-data.presenter';
import { ReaImportErrorPresenter } from '../presenters/rea-import-error.presenter';
import { ReaImportPayloadBuilder } from './rea-import-payload.builder';

import { ReaImportService } from 'src/app/service/fincas/rea-import.service';

import { BaseResponseModel } from 'src/app/models/base-response.model';
import {
  FincaReaRequest,
  ReaCheckResponse,
  ReaImportData,
  ReaMessageState,
  ReaModalModel,
  ReaRecoveryAction,
} from 'src/app/models/fincas/rea.model';

export type ReaCheckFlowStatus = 'ignored' | 'invalid-payload' | 'success' | 'error';

export type ReaImportFlowStatus = 'ignored' | 'invalid-payload' | 'completed';

export interface ReaCheckFlowResult {
  status: ReaCheckFlowStatus;
  response: BaseResponseModel<ReaImportData> | null;
  check: ReaCheckResponse | null;
  selectedRea: string;
}

export interface ReaImportFlowResult {
  status: ReaImportFlowStatus;
  response: BaseResponseModel<ReaImportData> | null;
}

export interface ReaCanImportContext {
  requiresReaSelection: boolean;
  hasSelectedAvailableRea: boolean;
  reaCount: number | null;
  hasReaCodeForImport: boolean;
  allowsTestFallbackImport: boolean;
}

/**
 * Owns the REA import/check request state so the modal component can focus on presentation and navigation.
 */
@Injectable()
export class ReaImportFlowFacade {
  public showSpinner = false;

  public messageState: ReaMessageState = '';

  public importResult: BaseResponseModel<ReaImportData> | null = null;

  public checkResult: BaseResponseModel<ReaImportData> | null = null;

  public selectedRea = '';

  public recoveryAction: ReaRecoveryAction | null = null;

  private readonly HTTP_UNPROCESSABLE_ENTITY = 422;

  constructor(private readonly reaImportService: ReaImportService) {}

  public canSend(): boolean {
    return this.importResult == null;
  }

  public canImport(context: ReaCanImportContext): boolean {
    if (this.showSpinner || this.importResult != null) {
      return false;
    }

    if (context.requiresReaSelection) {
      return context.hasSelectedAvailableRea;
    }

    if (context.reaCount === 0) {
      return context.hasReaCodeForImport || context.allowsTestFallbackImport;
    }

    return true;
  }

  public reset(): void {
    this.showSpinner = false;
    this.messageState = '';
    this.importResult = null;
    this.checkResult = null;
    this.recoveryAction = null;
    this.selectedRea = '';
  }

  public setSelectedRea(selectedRea: string): void {
    this.selectedRea = selectedRea.trim();
  }

  public checkAvailability(model: ReaModalModel): Observable<ReaCheckFlowResult> | null {
    if (this.showSpinner || this.importResult != null || this.checkResult != null) {
      return null;
    }

    const payload = this.buildPayload(model, 'check');

    if (payload == null) {
      this.markInvalidPayload();
      return null;
    }

    this.startCheckRequest();

    return this.reaImportService.checkRea(payload).pipe(
      take(1),
      map((response) => this.handleCheckResponse(response, model)),
      finalize(() => this.finishAsyncRequest()),
    );
  }

  public importRea(model: ReaModalModel): Observable<ReaImportFlowResult> | null {
    const payload = this.buildPayload(model, 'import', this.shouldAllowMissingReaForImport());

    if (payload == null) {
      this.markInvalidPayload();
      return null;
    }

    this.startImportRequest();

    return this.reaImportService.importRea(payload).pipe(
      take(1),
      map((response) => this.handleImportResponse(response)),
      finalize(() => this.finishAsyncRequest()),
    );
  }

  private buildPayload(
    model: ReaModalModel,
    operationType: 'check' | 'import',
    allowMissingRea = false,
  ): FincaReaRequest | null {
    return ReaImportPayloadBuilder.build(model, this.selectedRea, operationType, allowMissingRea);
  }

  private shouldAllowMissingReaForImport(): boolean {
    const data = this.asRecord(this.checkResult?.firstOrNull() ?? null);
    const summary = this.asRecord(data['summary']);

    return this.asBoolean(data['testFallbackEnabled'])
      || this.asBoolean(summary['testFallbackEnabled'])
      || this.asBoolean(summary['fegaValidationSkipped']);
  }

  private markInvalidPayload(): void {
    this.recoveryAction = null;
    this.messageState = 'warning';
  }

  private startCheckRequest(): void {
    this.showSpinner = true;
    this.messageState = 'checking';
    this.recoveryAction = null;
  }

  private startImportRequest(): void {
    this.showSpinner = true;
    this.messageState = '';
    this.importResult = null;
    this.recoveryAction = null;
  }

  private finishAsyncRequest(): void {
    this.showSpinner = false;
  }

  private handleCheckResponse(
    response: BaseResponseModel<ReaImportData>,
    model: ReaModalModel,
  ): ReaCheckFlowResult {
    const improved = this.withBetter422Message(response);
    const check = ReaImportDataPresenter.richCheckResponse(improved);

    this.checkResult = improved;

    if (!improved.isOk || check == null) {
      this.importResult = improved;
      this.messageState = 'error';
      this.recoveryAction = ReaImportErrorPresenter.recoveryAction(improved);

      return {
        status: 'error',
        response: improved,
        check: null,
        selectedRea: this.selectedRea,
      };
    }

    this.selectedRea = check.selectedRea ?? model.codigo_REA ?? '';
    this.messageState = check.requiresSelection ? 'selection' : '';
    this.recoveryAction = null;

    return {
      status: 'success',
      response: improved,
      check,
      selectedRea: this.selectedRea,
    };
  }

  private handleImportResponse(response: BaseResponseModel<ReaImportData>): ReaImportFlowResult {
    const improved = this.withBetter422Message(response);

    this.importResult = improved;
    this.messageState = improved.isOk ? 'success' : 'error';
    this.recoveryAction = improved.isOk ? null : ReaImportErrorPresenter.recoveryAction(improved);

    return {
      status: 'completed',
      response: improved,
    };
  }

  private withBetter422Message(response: BaseResponseModel<ReaImportData>): BaseResponseModel<ReaImportData> {
    if (response.statusCode !== this.HTTP_UNPROCESSABLE_ENTITY && response.isOk) {
      return response;
    }

    return response;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private asBoolean(value: unknown): boolean {
    return value === true;
  }
}